// Layout do módulo financeiro (TopBar + Sidebar) — PORTADO do StormFin
// (apps/web/src/components/layout/Layout.tsx). Mudanças do porte:
//   • trpc do V3 + namespace finance.*;
//   • rotas relativas ao nest /financeiro (/importar, /lancamentos, /kpis, /fechamento);
//   • logout do StormFin → botão "← ERP" que volta ao app do V3 (~/dashboard).
import { Fragment } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../../hooks/useAuth.js";
import { useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";
import { fmt } from "./finance-utils.js";
import { useMonth } from "./finance-month.js";
import { useFinanceCanEdit } from "./finance-access.js";

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    // Altura FIXA (100vh) + overflow hidden: a página não rola; cada coluna
    // (sidebar / main) rola por dentro. `minHeight: 0` libera o scroll dos
    // itens do grid (senão min-height:auto os deixa crescer com o conteúdo).
    <div
      style={{ display: "grid", gridTemplateRows: "56px 1fr", height: "100vh", overflow: "hidden" }}
    >
      <TopBar />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          minHeight: 0,
        }}
      >
        <Sidebar />
        <main style={{ padding: 24, overflowY: "auto", minHeight: 0 }}>{children}</main>
      </div>
    </div>
  );
}

function TopBar() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const tr = useT();
  // Só super admin edita; viewer (sócio) é somente-leitura → sem aba Importar.
  const canEdit = user?.isSuperAdmin ?? false;

  const tabs = [
    ...(canEdit ? [{ path: "/importar", label: tr("financeLayout.tabImport") }] : []),
    { path: "/lancamentos", label: tr("financeLayout.tabEntries") },
    { path: "/kpis", label: tr("financeLayout.tabKpis") },
    { path: "/fechamento", label: tr("financeLayout.tabClosing") },
  ];

  return (
    <header
      style={{
        background: "var(--bg2)",
        borderBottom: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        padding: "0 24px",
        gap: 20,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <div
        style={{
          fontFamily: "var(--mono)",
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: ".05em",
        }}
      >
        STORM<span style={{ color: "var(--accent)" }}>.</span>FIN
      </div>
      <nav style={{ display: "flex", gap: 2, marginLeft: "auto" }}>
        {tabs.map((t) => (
          <button
            key={t.path}
            className={`btn${location.startsWith(t.path) ? " active" : ""}`}
            style={
              location.startsWith(t.path)
                ? { background: "var(--bg3)", color: "var(--text)" }
                : { background: "none", border: "none", color: "var(--text2)" }
            }
            onClick={() => setLocation(t.path)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {/* Volta ao ERP (V3) — sai do módulo financeiro. `~` = caminho absoluto no wouter nested. */}
      <button
        className="btn"
        style={{ marginLeft: 8, fontSize: 12, opacity: 0.6 }}
        onClick={() => setLocation("~/dashboard")}
      >
        {tr("financeLayout.backToErp")}
      </button>
    </header>
  );
}

function Sidebar() {
  const { month } = useMonth();
  const t = useT();
  // F-finance-viewer-access — viewer (sócio) é somente-leitura: os % de
  // repartição na sidebar ficam desabilitados (a mutation já era superAdmin;
  // isto evita o campo "editável" que falhava silenciosamente no blur).
  const canEdit = useFinanceCanEdit();
  const { data: dashboard } = trpc.finance.dashboard.summary.useQuery();
  const { data: monthly = [] } = trpc.finance.dashboard.monthly.useQuery();
  const { data: cfg } = trpc.finance.settings.get.useQuery();
  const updateSettings = trpc.finance.settings.update.useMutation();

  const currency = cfg?.currency === "BRL" ? "R$" : cfg?.currency === "USD" ? "$" : "€";

  // Saldo de fechamento do mês selecionado (último mês com dados ≤ selecionado); fallback all-time.
  const closingRow = [...monthly].filter((m) => m.month <= month).pop();
  const b = closingRow?.contas ?? dashboard?.balances;
  const monthIdx = Number(month.split("-")[1] || "1") - 1;
  const ml =
    ([
      t("financeLayout.monthJan"),
      t("financeLayout.monthFeb"),
      t("financeLayout.monthMar"),
      t("financeLayout.monthApr"),
      t("financeLayout.monthMay"),
      t("financeLayout.monthJun"),
      t("financeLayout.monthJul"),
      t("financeLayout.monthAug"),
      t("financeLayout.monthSep"),
      t("financeLayout.monthOct"),
      t("financeLayout.monthNov"),
      t("financeLayout.monthDec"),
    ][monthIdx] ?? "??") +
    "/" +
    month.slice(2, 4);
  const accounts = [
    { id: "neto", label: "Neto", value: b?.["Neto"] ?? 0, color: "var(--neto)" },
    { id: "landione", label: "Landione", value: b?.["Landione"] ?? 0, color: "var(--landione)" },
    {
      id: "despesas",
      label: t("financeLayout.accountExpenses"),
      value: b?.["Despesas"] ?? 0,
      color: "var(--euro)",
    },
    {
      id: "tecnico",
      label: t("financeLayout.accountPayTechnician"),
      value: b?.["Pagar Técnico"] ?? 0,
      color: "var(--tecnico)",
    },
    // F-tesouraria-tva: TVA coletada − dedutível − paga ao Estado.
    { id: "tva", label: "TVA", value: b?.["TVA"] ?? 0, color: "var(--tva)" },
  ];

  // Caixa total = Σ das contas = saldo real dos bancos (conservação) — KPI.
  const caixaTotal = accounts.reduce((s, a) => s + a.value, 0);

  return (
    <aside
      style={{
        background: "var(--bg2)",
        borderRight: "1px solid var(--border)",
        padding: "20px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        overflowY: "auto",
        minHeight: 0,
      }}
    >
      {/* Contas virtuais */}
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--text3)",
            marginBottom: 10,
          }}
        >
          {t("financeLayout.virtualAccounts")} · {ml}
        </div>
        {accounts.map((a) => (
          <Fragment key={a.id}>
            <div
              style={{
                background: "var(--bg3)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r2)",
                padding: "14px 14px 12px",
                marginBottom: 8,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  background: a.color,
                }}
              />
              <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 4 }}>{a.label}</div>
              <div
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 18,
                  fontWeight: 500,
                  color: a.value > 0 ? "var(--green)" : a.value < 0 ? "var(--red)" : "var(--text)",
                }}
              >
                {fmt(a.value, currency)}
                {a.value < 0 ? " ⚠" : ""}
              </div>
            </div>

            {/* KPI: caixa total = Σ contas = saldo real dos bancos, após a última conta */}
            {a.id === "tecnico" && (
              <div
                style={{
                  background: "var(--bg4)",
                  border: "1px solid var(--accent)",
                  borderRadius: "var(--r2)",
                  padding: "14px 14px 12px",
                  marginBottom: 8,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--text)", fontWeight: 600 }}>
                    {t("financeLayout.totalCash")}
                  </span>
                  <span style={{ fontSize: 10, color: "var(--text3)" }}>
                    {t("financeLayout.sumBanks")}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: 20,
                    fontWeight: 600,
                    color:
                      caixaTotal > 0
                        ? "var(--green)"
                        : caixaTotal < 0
                          ? "var(--red)"
                          : "var(--text)",
                  }}
                >
                  {fmt(caixaTotal, currency)}
                  {caixaTotal < 0 ? " ⚠" : ""}
                </div>
              </div>
            )}
          </Fragment>
        ))}
      </div>

      {/* Parâmetros */}
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--text3)",
            marginBottom: 10,
          }}
        >
          {t("financeLayout.parameters")}
        </div>
        <div
          style={{
            background: "var(--bg3)",
            border: "1px solid var(--border)",
            borderRadius: "var(--r2)",
            padding: 14,
          }}
        >
          {[
            { label: "% Neto", key: "pctNeto", val: cfg?.pctNeto ?? 50 },
            { label: "% Landione", key: "pctLandione", val: cfg?.pctLandione ?? 50 },
          ].map((p) => (
            <div
              key={p.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--text2)" }}>{p.label}</span>
              <input
                type="number"
                min={0}
                max={100}
                defaultValue={p.val}
                disabled={!canEdit}
                style={{
                  background: "var(--bg4)",
                  border: "1px solid var(--border2)",
                  borderRadius: 4,
                  color: "var(--text)",
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                  width: 64,
                  padding: "4px 8px",
                  textAlign: "right",
                  opacity: canEdit ? 1 : 0.6,
                }}
                onBlur={(e) => {
                  if (!canEdit) return;
                  const v = Number(e.target.value);
                  updateSettings.mutate(
                    p.key === "pctNeto"
                      ? { pctNeto: v, pctLandione: 100 - v }
                      : { pctLandione: v, pctNeto: 100 - v },
                  );
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Resumo */}
      <div style={{ marginTop: "auto" }}>
        <SidebarSummary />
      </div>
    </aside>
  );
}

function SidebarSummary() {
  const t = useT();
  const { data } = trpc.finance.transactions.list.useQuery({ page: 1, pageSize: 1 });
  const { data: pending } = trpc.finance.transactions.list.useQuery({
    page: 1,
    pageSize: 1,
    type: "pending",
  });

  const total = data?.total ?? 0;
  const pendingCnt = pending?.total ?? 0;
  const classified = total - pendingCnt;

  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: "var(--text3)",
          marginBottom: 10,
        }}
      >
        {t("financeLayout.summary")}
      </div>
      <div
        style={{
          background: "var(--bg3)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r2)",
          padding: 14,
        }}
      >
        {[
          { label: t("financeLayout.tabEntries"), value: total, color: "var(--text)" },
          { label: t("financeLayout.rowClassified"), value: classified, color: "var(--green)" },
          { label: t("financeLayout.rowPending"), value: pendingCnt, color: "var(--amber)" },
        ].map((r) => (
          <div
            key={r.label}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 12, color: "var(--text2)" }}>{r.label}</span>
            <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: r.color }}>
              {r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
